import {numberValidator, requiredMessage, stringValidator} from "@/utils/yupValidators";
import * as yup from "yup";
import {gettext, interpolate, pgettext} from "@/utils/translate";
import dayjs from "dayjs";
import {PoliceVerification} from "@/components/hpo/choices";
import {isStandardBirthNumber} from "@/components/hpo/patientModal/tabs/identification/utils";

const now = dayjs()
const monthMessage = gettext('Month must be between 1 and 12')
const yearMessage = interpolate(gettext('Year can not be greater than %(thisYear)s'), {
    thisYear: now.year()
});
const countryRequiredMessage = pgettext('error', 'Select a country or mark that it cannot be determined');

export const triageSchemaOverrides = {
    ticPrefix: stringValidator.max(1),
    ticNumber: yup.string().nullable(),
    treatmentPriority: stringValidator,
};

export const identificationSchema = {
    countryUnknown: yup.boolean().nullable(),
    // The nationality question is now unconditionally required: a country, or the
    // explicit "cannot be determined" mark. The old `.when('note', …)` bypass is
    // deliberately gone — a filled Note used to silently drop the requirement.
    // Owner ruling: there is no connection between Note and any other field. gender
    // and isChild are unconditionally required (they mirror the NOT NULL model
    // columns and triage always sets both before identification is reachable).
    country: stringValidator.when('countryUnknown', {
        is: (val) => val === true,
        then: () => stringValidator,
        otherwise: () => stringValidator.required(countryRequiredMessage)
    }),
    gender: stringValidator.required(requiredMessage),
    isChild: yup.boolean().required(requiredMessage),
    // firstName, lastName and the three birth parts are the fields the dropped
    // ('hpo', first_name, last_name, birth_date) uniqueness on the old HpoIdentification model
    // was built from. They are required on the identification submit only — the model columns
    // stay nullable so triage can keep creating patients with no identity yet. Mirrors
    // IdentificationForm.REQUIRED_IDENTIFICATION_FIELDS on the backend; keep the two in step.
    firstName: stringValidator.required(requiredMessage),
    lastName: stringValidator.required(requiredMessage),
    birthDay: numberValidator
        .required(requiredMessage)
        .min(1, gettext('Date too low'))
        .test({
            name: 'maxDate',
            exclusive: false,
            params: {},
            message: gettext('Date too large'),
            test: function (value) {
                if (value && this.parent.birthMonth && this.parent.birthYear) {
                    const d = dayjs(this.parent.birthYear + '-' + this.parent.birthMonth + '-01')
                    return parseInt(value) <= d.daysInMonth()
                }
                return true
            }
        }),
    birthMonth: numberValidator.required(requiredMessage).min(1, monthMessage).max(12, monthMessage),
    birthYear: numberValidator.required(requiredMessage).max(now.year(), yearMessage),
    phoneNumber: numberValidator,
    note: stringValidator,
    policeVerification: yup.number().oneOf(Object.values(PoliceVerification)),
    nonStandardBirthNumber: yup.boolean().nullable(),
    birthNumber: stringValidator
        .transform((value) => value ? value : null)
        .matches(/^\d+$/, gettext("Birth number can contain only digits"))
        .when('nonStandardBirthNumber', {
            is: true,
            then: () => {
                return stringValidator
            },
            otherwise: () => {
                return stringValidator.test(
                    'standard-birth-number-test',
                    gettext("Invalid birth number"),
                    (value, context) => {
                        if (value) {
                            return isStandardBirthNumber(value)
                        }
                        return true
                    })
            }
        }),
    insurance: stringValidator,
    address: stringValidator,
    city: stringValidator.when('address', {
        is: (val) => {
            return !!val;
        },
        then: () => {
            return stringValidator.required(requiredMessage)
        },
        otherwise: () => {
            return stringValidator
        }
    }),
};

export function pickIdentificationValues(patientData) {
    return {
        country: patientData?.country,
        countryUnknown: patientData?.countryUnknown,
        gender: patientData?.gender,
        isChild: patientData?.isChild,
        firstName: patientData?.firstName,
        lastName: patientData?.lastName,
        birthDay: patientData?.birthDay,
        birthMonth: patientData?.birthMonth,
        birthYear: patientData?.birthYear,
        birthNumber: patientData?.birthNumber,
        nonStandardBirthNumber: patientData?.nonStandardBirthNumber,
        insurance: patientData?.insurance,
        address: patientData?.address,
        city: patientData?.city,
        phoneNumber: patientData?.phoneNumber,
        note: patientData?.note,
        policeVerification: patientData?.policeVerification,
    };
}
